import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Breadcrumbs,
  Link,
  Paper,
  Fade,
  Avatar,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { GetHeadings, Headings } from "../../constants/headings";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../store/auth/authAction";
import ConfirmModel from "../confirmModel/confirmModel";
import { R } from "../../constants/resources";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useHeader } from "../../context/HeaderContext";
import LogoutIcon from "@mui/icons-material/Logout";

export const Header = ({ onNavClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentHeadings = GetHeadings(location?.pathname);

  // Check if we're in edit mode for subscription (adjust breadcrumb dynamically)
  const isEditMode = location?.state?.editMode;
  const displayHeadings = { ...currentHeadings };
  if (isEditMode && displayHeadings?.title?.length > 0) {
    displayHeadings.title = [...displayHeadings.title];
    const lastIndex = displayHeadings.title.length - 1;
    if (displayHeadings.title[lastIndex] === "Add Subscription") {
      displayHeadings.title[lastIndex] = "Edit Subscription";
    }
  }

  const { admin } = useSelector((state) => state?.auth);
  const fname = admin?.data?.fname;
  const email = admin?.data?.email;

  const [showPopup, setShowPopup] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  const dispatch = useDispatch();
  const handleLogout = () => {
    dispatch(logout({ id: admin?.data?.id }));
  };

  const togglePopup = () => {
    setShowPopup((prev) => !prev);
  };

  const handleRemove = () => {
    setShowPopup(false);
  };

  const { headerNumber, setHeaderNumber, listCountHeader, setListCountHeader } =
    useHeader();

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (index) => {
    // Special case: If clicked on "Dealers" (first breadcrumb item), navigate to Dealer List
    const clickedTitle = displayHeadings.title[index];
    if (clickedTitle === "Dealers") {
      const dealerListRoute = Headings.find(
        (item) =>
          item.title.length === 2 &&
          item.title[0] === "Dealers" &&
          item.title[1] === "Dealer List",
      );
      if (dealerListRoute) {
        navigate(dealerListRoute.route);
        return;
      }
    }

    if (index === 0) {
      navigate("/");
      return;
    }

    // Use original currentHeadings for navigation, not displayHeadings
    const targetTitlePath = currentHeadings.title.slice(0, index + 1);

    // Find the route that matches this title path exactly
    const targetRouteConfig = Headings.find((item) => {
      return (
        item.title.length === targetTitlePath.length &&
        item.title.every((val, i) => val === targetTitlePath[i])
      );
    });

    if (targetRouteConfig) {
      navigate(targetRouteConfig.route);
    }
  };

  useEffect(() => {
    if (!headerNumber && location.pathname?.includes("dealers")) {
      setHeaderNumber("");
      setListCountHeader("");
    }
  }, [location.pathname, headerNumber, setHeaderNumber, setListCountHeader]);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "52px",
        display: "flex",
        alignItems: "center",
        padding: "0 28px",
        backgroundColor: "#f3f4f6",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Breadcrumbs
            separator={
              <Typography sx={{ color: "#9599a3" }}>
                <ChevronRightIcon sx={{ fontSize: 13 }} />
              </Typography>
            }
            aria-label="breadcrumb"
            sx={{ fontSize: 14 }}
          >
            {displayHeadings?.title?.map((heading, index) => (
              <Link
                key={index}
                underline="hover"
                sx={{
                  cursor: "pointer",
                  color:
                    index === displayHeadings.title.length - 1
                      ? "#14151a"
                      : "#9599a3",
                  fontWeight:
                    index === displayHeadings.title.length - 1 ? 600 : 500,
                }}
                onClick={() => handleBreadcrumbClick(index)}
              >
                {heading}
                {displayHeadings.title.length - 1 === index &&
                  listCountHeader &&
                  ` (${listCountHeader})`}
              </Link>
            ))}

            {headerNumber && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: (theme) => theme.palette.text.primary }}
                >
                  {headerNumber}
                </Typography>
              </Box>
            )}
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Tooltip title="Notifications">
            <IconButton
              sx={{
                padding: "6px",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              }}
            >
              <img
                src={R.ic_notification}
                alt="Notification icon"
                style={{ width: 20, height: 20 }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Profile">
            <IconButton
              onClick={togglePopup}
              sx={{
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
                width: 32,
                height: 32,
                borderRadius: "50%",
              }}
            >
              {" "}
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background: "linear-gradient(135deg, #007abe, #00b2e3)",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {fname ? fname[0].toUpperCase() : "U"}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {showPopup && (
        <Fade in={showPopup} timeout={300}>
          <Paper
            sx={{
              position: "absolute",
              top: "7%",
              right: 16,
              mt: 1,
              backgroundColor: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.shadows[8],
              borderRadius: 2,
              p: 2,
              zIndex: 1300, // Higher zIndex to ensure it appears above other elements
              minWidth: 240,
              maxWidth: 300,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
            role="dialog"
            aria-labelledby="logout-popup-title"
            aria-describedby="logout-popup-description"
          >
            {/* User Info Section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  background: "linear-gradient(135deg, #007abe, #00b2e3)",
                  fontSize: 20,
                  fontWeight: 600,
                }}
              >
                {fname ? fname[0].toUpperCase() : "U"}
              </Avatar>
              <Box>
                <Typography
                  id="logout-popup-title"
                  variant="h6"
                  sx={{ fontWeight: 600, fontSize: 16 }}
                >
                  {fname || "User"}
                </Typography>
                <Typography
                  id="logout-popup-description"
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: 13,
                  }}
                >
                  {email || "user@example.com"}
                </Typography>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "flex-end",
                mt: 1,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleRemove}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: 1,
                  color: (theme) => theme.palette.text.primary,
                  borderColor: (theme) => theme.palette.divider,
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.action.hover,
                    borderColor: (theme) => theme.palette.primary.main,
                  },
                }}
                aria-label="Cancel logout"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleRemove();
                  setConfirmLogout(true);
                }}
                startIcon={<LogoutIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: 1,
                  backgroundColor: (theme) => theme.palette.error.main,
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.error.dark,
                  },
                }}
                aria-label="Confirm logout"
              >
                Logout
              </Button>
            </Box>
          </Paper>
        </Fade>
      )}

      {confirmLogout && (
        <ConfirmModel
          heading={"Logout"}
          message={"Are you sure you want to log out?"}
          onSubmit={handleLogout}
          onClose={() => setConfirmLogout(false)}
        />
      )}
    </Box>
  );
};

export default Header;
